import { Component, DoCheck, ElementRef, EventEmitter, Input, IterableDiffer, IterableDiffers, OnDestroy, OnInit, Output } from '@angular/core';
import { PaginatedDTO } from 'src/app/Common/common.interface';

@Component({
  selector: 'app-pagination',
  templateUrl: './pagination.component.html',
  styleUrls: ['./pagination.component.css']
})
export class PaginationComponent implements OnInit, DoCheck, OnDestroy {
  @Input() rowData: PaginatedDTO[] = [];
  @Input() EnablePageSize: boolean = false
  @Input() EnableNavigation: boolean = false
  @Input() Disabled: boolean = false


  private rowDataDiffer: IterableDiffer<any>;

  constructor(private differs: IterableDiffers, private host: ElementRef<HTMLElement>) {
    this.rowDataDiffer = differs.find([]).create();
  }


  @Output() pageSizeChanged: EventEmitter<{ currentPage: number, pageSize: number }> = new EventEmitter<{ currentPage: number, pageSize: number }>();

  currentPage: number = 1
  totalPages: number = 0
  pageSize: number = 10
  totalRecords: number = 0
  start: number = 0
  end: number = 0

  /** Pagers currently on screen, so a page-size row and a navigation row for the same list can find each other. */
  private static readonly live = new Set<PaginationComponent>();

  /**
   * Host class set on both pagers when one shows the page-size row and the other the navigation row for the same `rowData`
   * (the usual page layout: size row above the table, navigation row below it). On phones the CSS then hides the size row
   * and shows the size selector inside the navigation row. A pager without such a partner behaves the same on every screen.
   * It is a plain DOM class (not a template binding) so the pair is marked in the same check, whichever of them runs first.
   */
  private static readonly twinClass = 'pg-twin';




  onPageSizeChanged(event: any) {
    if (this.Disabled) {
      return;
    }

    const selectedValue = event.target.value;
    this.pageSize = selectedValue
    this.pageSizeChanged.emit({ currentPage: 1, pageSize: selectedValue })
  }

  goToFirstPage() {
    if (this.Disabled) {
      return;
    }

    if (this.currentPage !== 1) {
      this.currentPage = 1
      this.pageSizeChanged.emit({ currentPage: this.currentPage, pageSize: this.pageSize })
    }

  }

  goToPreviousPage() {
    if (this.Disabled) {
      return;
    }

    if (this.currentPage > 1) {
      this.currentPage = this.currentPage - 1;
      this.pageSizeChanged.emit({ currentPage: this.currentPage, pageSize: this.pageSize })

    }
  }


  getPagesToShow(): number[] {
    const numPagesToShow = 5;
    const halfNumPages = Math.floor(numPagesToShow / 2);
    const startPage = Math.max(1, this.currentPage - halfNumPages);
    const endPage = Math.min(this.totalPages, startPage + numPagesToShow - 1);

    if (startPage <= this.currentPage && this.currentPage <= endPage) {
      return Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i);
    }
    return Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i);
  }

  goToNumberPage(data: any) {
    if (this.Disabled) {
      return;
    }

    this.currentPage = data
    this.pageSizeChanged.emit({ currentPage: this.currentPage, pageSize: this.pageSize })
  }

  goToNextPage() {
    if (this.Disabled) {
      return;
    }

    if (this.currentPage < this.totalPages) {
      this.currentPage = this.currentPage + 1;
      this.pageSizeChanged.emit({ currentPage: this.currentPage, pageSize: this.pageSize })

    }
  }

  goToLastPage() {
    if (this.Disabled) {
      return;
    }

    if (this.currentPage !== this.totalPages) {
      this.currentPage = this.totalPages
      this.pageSizeChanged.emit({ currentPage: this.currentPage, pageSize: this.pageSize })

    }
  }


  ngOnInit(): void {
    PaginationComponent.live.add(this);
  }

  ngDoCheck(): void {
    const changes = this.rowDataDiffer.diff(this.rowData);
    if (changes) {
      this.updatePaginationState();
    }
    this.syncTwin();
  }

  ngOnDestroy(): void {
    PaginationComponent.live.delete(this);
    this.host.nativeElement.classList.remove(PaginationComponent.twinClass);
  }

  private syncTwin(): void {
    const twin = this.findTwin();
    this.host.nativeElement.classList.toggle(PaginationComponent.twinClass, !!twin);
    twin?.host.nativeElement.classList.add(PaginationComponent.twinClass);
  }

  private findTwin(): PaginationComponent | null {
    const sizeOnly = (p: PaginationComponent) => p.EnablePageSize && !p.EnableNavigation;
    const navOnly = (p: PaginationComponent) => !p.EnablePageSize && p.EnableNavigation;
    if (!sizeOnly(this) && !navOnly(this)) {
      return null;
    }
    for (const p of PaginationComponent.live) {
      if (p !== this && p.rowData === this.rowData && (sizeOnly(this) ? navOnly(p) : sizeOnly(p))) {
        return p;
      }
    }
    return null;
  }

  private updatePaginationState(): void {
    if (this.rowData && this.rowData.length > 0) {
      this.totalPages = Math.ceil(this.rowData[0].totalCount! / this.rowData[0].pageSize);
      this.currentPage = this.rowData[0].pageNo;
      this.pageSize = this.rowData[0].pageSize;
      this.totalRecords = this.rowData[0].totalCount ?? 0;
      this.start = this.rowData[0].rowNum ?? 0;
      this.end = this.rowData[this.rowData.length - 1].rowNum ?? 0;
    } else {
      this.totalPages = 0;
      this.currentPage = 1;
      this.totalRecords = 0;
      this.start = 0;
      this.end = 0;
    }
  }
}
